import type { jsPDF } from "jspdf";
import { encodeQR } from "@/shared/utils/qr/encodeQR";

type Run = { start: number; end: number };

/** The dark stretches of one row, as half-open column intervals. */
function darkRuns(row: boolean[], size: number): Run[] {
  const runs: Run[] = [];
  let start = -1;

  for (let column = 0; column <= size; column += 1) {
    const isDark = column < size && row[column];

    if (isDark && start === -1) start = column;
    if (isDark || start === -1) continue;

    runs.push({ start, end: column });
    start = -1;
  }

  return runs;
}

/**
 * Draws the AR link as a vector QR code.
 *
 * Rasterising it would blur the modules at print resolution and cost far more
 * bytes than the few hundred filled rectangles this produces. Neighbouring dark
 * modules are merged into one rectangle — first along the row, then down the
 * rows that repeat the same run — so a typical code ends up as ~240 rectangles
 * rather than one per dark module, of which there are ~600.
 *
 * The merge is not about the byte count: two rectangles that share an edge are
 * antialiased independently, and at the one to two device pixels per module a
 * PDF viewer gives this symbol at 100% zoom, that seam shows up as a light
 * hairline through a dark block. Every seam removed is a hairline removed.
 */
export function drawQRCode(
  doc: jsPDF,
  value: string,
  box: { x: number; y: number; size: number },
  color: string,
): void {
  const { size, modules } = encodeQR(value);
  const moduleSize = box.size / size;

  doc.setFillColor(color);

  const fill = (run: Run, top: number, bottom: number) =>
    doc.rect(
      box.x + run.start * moduleSize,
      box.y + top * moduleSize,
      (run.end - run.start) * moduleSize,
      (bottom - top) * moduleSize,
      "F",
    );

  // Runs that have not ended yet, keyed by the columns they span so a row can
  // tell whether it repeats one and should extend it instead of starting anew.
  let open = new Map<string, Run & { top: number }>();

  for (let row = 0; row <= size; row += 1) {
    const continued = new Map<string, Run & { top: number }>();

    for (const run of row < size ? darkRuns(modules[row], size) : []) {
      const key = `${run.start}:${run.end}`;
      continued.set(key, open.get(key) ?? { ...run, top: row });
      open.delete(key);
    }

    // Whatever the row did not repeat stops here.
    for (const rect of open.values()) fill(rect, rect.top, row);
    open = continued;
  }
}
